import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { apiService } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';
import { API_ENDPOINTS } from '@clarix/utils';
import { useActiveWorkspaceSlug } from '../getWorkspaceSlug';

/** A starred sidebar row. `id` is panel-scoped; `label`/`to` are a snapshot. */
export interface SidebarFavourite {
  id: string;
  label: string;
  to: string;
}

type FavouritePanels = Readonly<Record<string, readonly SidebarFavourite[]>>;

/** Mirrors the backend's per-panel cap. */
const MAX_PER_PANEL = 50;
const EMPTY: readonly SidebarFavourite[] = [];

// Server is the source of truth; localStorage only paints the section before the GET lands.
let panels: FavouritePanels = {};
let cacheKey: string | null = null;
let hydratedFor: string | null = null;
// Bumped on every local edit so a slower, older response can't overwrite a newer star.
let editVersion = 0;
const listeners = new Set<() => void>();

function isFavourite(value: unknown): value is SidebarFavourite {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.label === 'string' && typeof v.to === 'string';
}

function sanitize(raw: unknown): FavouritePanels {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, SidebarFavourite[]> = {};
  for (const [panelId, list] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(list)) out[panelId] = list.filter(isFavourite);
  }
  return out;
}

function publish(next: FavouritePanels) {
  panels = next;
  if (cacheKey) {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(next));
    } catch {
      /* quota / private mode — the server copy still holds */
    }
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function hydrate(key: string) {
  if (hydratedFor === key) return;
  hydratedFor = key;
  cacheKey = `clarix-sidebar-favourites:${key}`;
  let cached: FavouritePanels = {};
  try {
    cached = sanitize(JSON.parse(localStorage.getItem(cacheKey) ?? '{}'));
  } catch {
    /* corrupt cache — start empty */
  }
  publish(cached);

  const startedAt = editVersion;
  try {
    const res = await apiService.get<{ panels: unknown }>(API_ENDPOINTS.SIDEBAR_FAVOURITES);
    if (hydratedFor === key && editVersion === startedAt) publish(sanitize(res?.panels));
  } catch {
    /* offline or not deployed yet — keep the cached copy */
  }
}

function savePanel(panelId: string, items: SidebarFavourite[]) {
  editVersion += 1;
  const version = editVersion;
  const next: Record<string, readonly SidebarFavourite[]> = { ...panels, [panelId]: items };
  if (items.length === 0) delete next[panelId];
  publish(next);

  apiService
    .put<{ panels: unknown }>(
      `${API_ENDPOINTS.SIDEBAR_FAVOURITES}/${encodeURIComponent(panelId)}`,
      { items },
    )
    .then((res) => {
      if (version === editVersion) publish(sanitize(res?.panels));
    })
    .catch(() => {
      /* keep the optimistic star; the next load reconciles with the server */
    });
}

/** One panel's starred rows (in starring order) plus a star/unstar toggle. */
export function useSidebarFavourites(panelId: string) {
  const all = useSyncExternalStore(
    subscribe,
    () => panels,
    () => panels,
  );

  const email = useAuthStore((s) => s.user?.email ?? null);
  const slug = useActiveWorkspaceSlug();
  useEffect(() => {
    if (email) void hydrate(`${slug ?? 'apex'}:${email}`);
  }, [email, slug]);

  const toggle = useCallback(
    (fav: SidebarFavourite) => {
      const current = panels[panelId] ?? EMPTY;
      if (current.some((f) => f.id === fav.id)) {
        savePanel(
          panelId,
          current.filter((f) => f.id !== fav.id),
        );
      } else if (current.length < MAX_PER_PANEL) {
        savePanel(panelId, [...current, fav]);
      }
    },
    [panelId],
  );

  return { items: all[panelId] ?? EMPTY, toggle };
}
